import { DoneIcon } from '@econsult/econsult-ui/dist/assets';
import { PopOut, PopOutBody } from '@econsult/econsult-ui/dist/components/PopOut';
import { Option, SelectOptions } from '@econsult/econsult-ui/dist/components/Select';
import { Caret } from '@econsult/econsult-ui/dist/components/TopBar/TopBarStyles';
import Button from 'components/Button';
import { FlexColumnDiv, FlexDiv } from 'components/Div/styles';
import {
  FooterManageButtonContainer,
  ListItemContainer,
  PopOutScrollView,
  TemplateNameText,
} from 'components/TemplateManager/styles';
import { STRINGS } from 'config';
import React, { useState } from 'react';
import { useAppDispatch, useAppState } from 'store/configureStore';
import { AiSnomedEntry } from 'store/reducer/snomed';
import styled from 'styled-components';
import { LightInput } from 'styles/elements/inputs';
import { ParagraphSmallRegular } from 'styles/elements/typography';
import GenericModal from '../Modal/GenericModal';
import * as styles from './styles';

const {
  REDUCERS: {
    SNOMED: { SET_PROBLEM_SNOMED_CODE },
  },
  TEMPLATE: { EMIS_WEB_TEMPLATE },
} = STRINGS;

export const Tick = styled(DoneIcon)`
  height: 13px;
  width: 13px;
  color: ${({ theme }) => theme.colorStatusSuccess};
`;

export const SortOptions = styled(SelectOptions)`
  top: calc(100% + 5px);
  width: 440px;
`;

export const SortOption = styled(Option)`
  color: ${({ theme, disabled }) => (disabled ? `${theme.colorNeutralNeutral7}` : '')};
  padding-left: 7px;
`;

export const SelectOptionDisplay = styled(FlexDiv)`
  align-items: center;
`;

export const StyledPopOutBody = styled(PopOutBody)`
  width: 600px;
  max-width: 600px;
  overflow-x: hidden;
  && {
    padding: 0;
    animation: appear 0.2s ease;
  }

  @keyframes appear {
    from {
      transform: translateY(0.5rem);
      opacity: 0;
    }

    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

function findDisorders(data: AiSnomedEntry[]): AiSnomedEntry[] {
  return data.filter(item => item.info.toLowerCase().includes('(disorder)'));
}

const ModalWithSnomedCodes = ({
  isOpen,
  setOpen,
  disorders,
  onSelect,
  setManualModalOpen,
}: {
  isOpen: boolean;
  setOpen: (open: boolean) => void;
  disorders: AiSnomedEntry[];
  onSelect: (snomed: AiSnomedEntry) => void;
  setManualModalOpen: (open: boolean) => void;
}) => {
  const {
    featureFlags: { SUMMARISER_MANUAL_SNOMED_ENABLED },
  } = useAppState();
  const [template, setTemplate] = useState<AiSnomedEntry | null>(null);

  const onSelectTemplate = (option: AiSnomedEntry) => {
    setTemplate(option);
    onSelect(option);
    setOpen(false);
  };

  return (
    <PopOut
      alignBlock="start"
      alignInline="start"
      isOpen={isOpen}
      setIsOpen={setOpen}
      closeOnClickOutside
      automationName="ModalWithSnomedCodesPopOut"
    >
      <StyledPopOutBody automationName="ModalWithSnomedCodesStyledPopOut">
        <PopOutScrollView style={{ width: '100%' }}>
          {disorders && disorders.length > 0 ? (
            disorders.map(option => {
              const { code, info } = option;
              return (
                <ListItemContainer
                  key={code}
                  data-automation="TemplateListItemContainer"
                  onClick={() => onSelectTemplate(option)}
                  isHighlighted={template?.code === option.code}
                  style={{ width: '600px' }}
                >
                  <TemplateNameText style={{ textOverflow: 'initial', maxWidth: '600px' }}>
                    {code} - {info}
                  </TemplateNameText>
                </ListItemContainer>
              );
            })
          ) : (
            <ParagraphSmallRegular style={{ margin: '8px' }}>No results</ParagraphSmallRegular>
          )}
        </PopOutScrollView>
        {SUMMARISER_MANUAL_SNOMED_ENABLED && (
          <FooterManageButtonContainer>
            <Button
              buttonText="Add code"
              id="Template Manage Button"
              state="secondary"
              onClick={() => setManualModalOpen(true)}
              disabled={false}
              style={{ padding: 0, outline: 'none' }}
            />
          </FooterManageButtonContainer>
        )}
      </StyledPopOutBody>
    </PopOut>
  );
};

interface HeadingDropDownProps {
  disorders: AiSnomedEntry[];
}

const HeadingDropDown = ({ disorders }: HeadingDropDownProps) => {
  const [problemHeading, setProblemHeading] = useState<AiSnomedEntry | null>(null);
  const [snomedModalOpen, setSnomedModalOpen] = useState(false);
  const [manualModalOpen, setManualModalOpen] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [manualInfo, setManualInfo] = useState('');
  const dispatch = useAppDispatch();

  function handleSetProblemHeading(snomed: AiSnomedEntry) {
    dispatch({ type: SET_PROBLEM_SNOMED_CODE, payload: snomed });
    setProblemHeading(snomed);
  }
  function handleManualSubmit() {
    if (manualCode.trim() && manualInfo.trim()) {
      const manualEntry: AiSnomedEntry = { code: manualCode.trim(), info: manualInfo.trim(), unit: null, value: null };
      dispatch({ type: SET_PROBLEM_SNOMED_CODE, payload: manualEntry });
      setProblemHeading(manualEntry);
      setManualModalOpen(false);
      setManualCode('');
      setManualInfo('');
    }
  }
  function handleCloseManualModal() {
    setManualModalOpen(false);
    setManualCode('');
    setManualInfo('');
  }
  return (
    <FlexDiv style={{ alignItems: 'center', marginTop: '4px' }} data-automation="Summary-Complaint-Heading-Outer-Div">
      <FlexColumnDiv style={{ gap: '4px' }} data-automation="Summary-Complaint-Heading">
        <button
          type="button"
          onClick={() => setSnomedModalOpen(true)}
          style={{ all: 'unset', cursor: 'pointer' }}
          aria-label="Select problem heading"
        >
          Problem: {(problemHeading && `${problemHeading.code} - ${problemHeading.info}`) || '[Please select]'}
          {!problemHeading?.code && <Caret />}
        </button>
        <ModalWithSnomedCodes
          isOpen={snomedModalOpen}
          setOpen={setSnomedModalOpen}
          disorders={disorders}
          onSelect={handleSetProblemHeading}
          setManualModalOpen={setManualModalOpen}
        />
      </FlexColumnDiv>
      <GenericModal
        isOpen={manualModalOpen}
        onClose={handleCloseManualModal}
        title="Problem: manual SNOMED entry"
        showCloseButton
      >
        <div style={{ padding: '20px' }}>
          <div style={{ marginBottom: '10px' }}>
            <label htmlFor="snomed-code">SNOMED code:</label>
            <LightInput
              id="snomed-code"
              type="text"
              value={manualCode}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setManualCode(e.target.value)}
              style={{ width: '100%', marginTop: '5px' }}
            />
          </div>
          <div style={{ marginBottom: '20px' }}>
            <label htmlFor="snomed-description">SNOMED description:</label>
            <LightInput
              id="snomed-description"
              type="text"
              value={manualInfo}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setManualInfo(e.target.value)}
              style={{ width: '100%', marginTop: '5px' }}
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <Button
              id="manual-snomed-code-modal-cancel-modal-button"
              onClick={handleCloseManualModal}
              buttonText="Cancel"
              icon={null}
              iconPosition="left"
              state="secondary"
              disabled={false}
            />
            <Button
              id="manual-snomed-code-modal-add-modal-button"
              onClick={handleManualSubmit}
              buttonText="Add"
              icon={null}
              iconPosition="left"
              state="primary"
              disabled={!manualCode.trim() || !manualInfo.trim()}
            />
          </div>
        </div>
      </GenericModal>
    </FlexDiv>
  );
};

export const SummaryComplaintHeading = () => {
  const {
    app: { clinicalSystemName },
    summary: { currentSummary, presentingComplaintHeading },
    snomed: { aiSnomedCodes },
    templates: {
      template: { name },
    },
  } = useAppState();
  const disorders = aiSnomedCodes && aiSnomedCodes.length ? findDisorders(aiSnomedCodes) : null;
  if (name === EMIS_WEB_TEMPLATE && currentSummary && clinicalSystemName === 'EMIS') {
    return <HeadingDropDown disorders={disorders || []} />;
  }
  return (
    <>
      {currentSummary && (
        <styles.PresentingComplaintHeading data-automation="Summary-Complaint-Heading">
          {presentingComplaintHeading || 'Presenting complaint not specified'}
        </styles.PresentingComplaintHeading>
      )}
    </>
  );
};
